<template>
    <base-dialog :value="dialog" :title="title" :width="380" @input="onInput" @close="hide">
        <div class="form-group">
            <span class="form-label">{{ $t('common.name') }}</span>
            <kimsa-text-field :placeholder="$t('business.activity_placeholder')" :value="name" @change="name = $event"
                dense force-value />
        </div>

        <div class="form-group mt-4">
            <span class="form-label">{{ $t('business.color') }}</span>
            <div class="color-swatches">
                <button v-for="option in palette" :key="option" type="button" class="color-swatch"
                    :class="{ selected: option === color }" :style="{ background: option }"
                    @click="color = option" />
            </div>
        </div>

        <span v-if="blockedText" class="activity-warning">{{ blockedText }}</span>

        <template v-slot:footer>
            <dialog-actions :confirm-label="confirmLabel" :disabled="!valid" @cancel="hide" @confirm="save">
                <template v-slot:extra>
                    <button v-if="isEdit" type="button" class="activity-delete" :disabled="!!blockedText"
                        @click="remove">
                        {{ $t('common.delete') }}
                    </button>
                </template>
            </dialog-actions>
        </template>
        <kimsa-confirm ref="deleteConfirm" :title="$t('business.delete_activity')"
            :content="$t('business.delete_activity_body', { name: name })" :persistent="false" alert-mode
            show-cancel @confirm="onConfirmDelete" />
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { ACTIVITY_PALETTE } from "@/utils/Business";

export default {
    name: "ActivityDialog",
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaConfirm },
    props: {
        usedCount: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            dialog: false,
            activity: null,
            name: '',
            color: ACTIVITY_PALETTE[0],
            palette: ACTIVITY_PALETTE,
        }
    },
    computed: {
        isEdit(vm = this) {
            return !!vm.activity
        },
        valid(vm = this) {
            return !!String(vm.name || '').trim()
        },
        title(vm = this) {
            return vm.isEdit ? vm.$t('business.edit_activity') : vm.$t('business.new_activity')
        },
        confirmLabel(vm = this) {
            return vm.isEdit ? vm.$t('common.save') : vm.$t('common.create')
        },
        blockedText(vm = this) {
            if (!vm.isEdit || vm.usedCount === 0) return ''
            return vm.$tc('business.activity_in_use', vm.usedCount, { n: vm.usedCount })
        },
    },
    methods: {
        show(activity = null) {
            const vm = this
            vm.activity = activity
            vm.name = activity?.name || ''
            vm.color = activity?.color || ACTIVITY_PALETTE[0]
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        save() {
            const vm = this
            if (!vm.valid) return
            vm.$emit('save', {
                id: vm.activity?.id || null,
                name: String(vm.name).trim(),
                color: vm.color,
            })
            vm.hide()
        },
        remove() {
            if (this.blockedText) return
            this.$refs.deleteConfirm.show()
        },
        onConfirmDelete() {
            const vm = this
            vm.$emit('delete', vm.activity)
            vm.hide()
        },
    },
}
</script>

<style scoped>
.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.color-swatches {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.color-swatch {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid transparent;
    transition: border-color 0.15s, transform 0.15s;
}

.color-swatch.selected {
    border-color: var(--v-txtPrimary-base);
    transform: scale(1.12);
}

.activity-warning {
    display: block;
    margin-top: 14px;
    font-size: 11px;
    color: var(--v-error-base);
}

.activity-delete {
    padding: 0 20px;
    height: 52px;
    border-right: 1px solid var(--v-outline-base);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--v-error-base);
}

.activity-delete:disabled {
    opacity: 0.4;
    cursor: default;
}
</style>
